import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  Lang: i18n.language,
});

export const fetchCostCenterAccounts = createAsyncThunk(
  "manual_journal_entries/fetchCostCenterAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/cost-center-accounts`, {
        headers: { ...headers(), "Content-Type": "application/json" },
      });
      return Array.isArray(data?.data) ? data.data : [];
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to load cost center accounts"
      );
    }
  }
);

const appendIfPresent = (params, key, value) => {
  if (value === "" || value == null || value === undefined) return;
  params.append(key, String(value));
};

export const buildManualJournalEntriesParams = (filters = {}) => {
  const params = new URLSearchParams();
  appendIfPresent(params, "page", filters.page);
  appendIfPresent(params, "per_page", filters.per_page);
  appendIfPresent(params, "type", filters.type);
  appendIfPresent(params, "statement", filters.statement);
  appendIfPresent(params, "created_user_id", filters.created_user_id);
  appendIfPresent(params, "date_filter", filters.date_filter);
  appendIfPresent(params, "journal_entrie_number", filters.journal_entrie_number);
  appendIfPresent(params, "reference_number", filters.reference_number);

  const accounts = Array.isArray(filters.account)
    ? filters.account
    : filters.account
      ? [filters.account]
      : [];
  accounts.forEach((id, index) => {
    if (id !== "" && id != null) {
      params.append(`account[${index}]`, String(id));
    }
  });

  if (filters.date_filter === "range") {
    appendIfPresent(params, "date_from", filters.date_from);
    appendIfPresent(params, "date_to", filters.date_to);
  }

  return params;
};

const normalizeListPayload = (data) => {
  const root = data?.data ?? data;
  if (Array.isArray(root)) {
    return { list: root, pagination: data?.pagination ?? null };
  }
  if (Array.isArray(root?.data)) {
    return {
      list: root.data,
      pagination: root.pagination ?? data?.pagination ?? null,
    };
  }
  return { list: [], pagination: null };
};

export const fetchManualJournalEntries = createAsyncThunk(
  "manual_journal_entries/fetchList",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = buildManualJournalEntriesParams(filters);
      const qs = params.toString();
      const { data } = await axios.get(
        `${BASE_URL}/manual-journal-entries${qs ? `?${qs}` : ""}`,
        {
          headers: { ...headers(), "Content-Type": "application/json" },
        }
      );
      return normalizeListPayload(data);
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to load manual journal entries"
      );
    }
  }
);

export const fetchManualJournalEntryDetails = createAsyncThunk(
  "manual_journal_entries/fetchDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${BASE_URL}/manual-journal-entries/${id}`,
        { headers: { ...headers(), "Content-Type": "application/json" } }
      );
      return data?.data ?? null;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to load entry details"
      );
    }
  }
);

export const addManualJournalEntry = createAsyncThunk(
  "manual_journal_entries/add",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/manual-journal-entries`,
        formData,
        {
          headers: headers(),
        }
      );
      return data;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to create manual journal entry"
      );
    }
  }
);

export const updateManualJournalEntry = createAsyncThunk(
  "manual_journal_entries/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      formData.append("id", String(id));
      const { data } = await axios.post(
        `${BASE_URL}/manual-journal-entries/update`,
        formData,
        { headers: headers() }
      );
      return data;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to update manual journal entry"
      );
    }
  }
);

export const deleteManualJournalEntry = createAsyncThunk(
  "manual_journal_entries/delete",
  async (payload, { rejectWithValue }) => {
    try {
      const journal_entry_id =
        payload && typeof payload === "object"
          ? payload.journal_entry_id ?? payload.id
          : payload;
      const delete_reason =
        payload && typeof payload === "object" ? payload.delete_reason : "";
      const fd = new FormData();
      fd.append("journal_entry_id", journal_entry_id);
      if (delete_reason) fd.append("delete_reason", delete_reason);
      const { data } = await axios.post(
        `${BASE_URL}/manual-journal-entries/delete`,
        fd,
        { headers: headers() }
      );
      return data;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to delete entry"
      );
    }
  }
);

export const bulkApplyModified = createAsyncThunk(
  "manual_journal_entries/bulkApplyModified",
  async (ids, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      ids.forEach((id, i) => {
        fd.append(`journal_entry_id[${i}]`, String(id));
      });
      const { data } = await axios.post(
        `${BASE_URL}/manual-journal-entries/bulk-apply-modified`,
        fd,
        { headers: headers() }
      );
      return data;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to apply modified entries"
      );
    }
  }
);

export const bulkDeleteManualJournalEntries = createAsyncThunk(
  "manual_journal_entries/bulkDelete",
  async (ids, { rejectWithValue }) => {
    try {
      const params = {};
      (ids || []).forEach((entryId, i) => {
        params[`journal_entry_id[${i}]`] = String(entryId);
      });
      const { data } = await axios.post(
        `${BASE_URL}/manual-journal-entries/bulk-delete`,
        null,
        { headers: headers(), params }
      );
      return data;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || "Failed to delete entries"
      );
    }
  }
);

const slice = createSlice({
  name: "manual_journal_entries",
  initialState: {
    list: [],
    pagination: null,
    details: null,
    costCenterAccounts: [],
    isLoading: false,
    isLoadingDetails: false,
    error: null,
    success: null,
    lastSavedId: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.lastSavedId = null;
    },
    clearDetails: (state) => {
      state.details = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCostCenterAccounts.fulfilled, (state, { payload }) => {
        state.costCenterAccounts = payload;
      })
      .addCase(fetchManualJournalEntries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManualJournalEntries.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.list = Array.isArray(payload?.list) ? payload.list : [];
        state.pagination = payload?.pagination ?? null;
      })
      .addCase(fetchManualJournalEntries.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(fetchManualJournalEntryDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.error = null;
        state.details = null;
      })
      .addCase(fetchManualJournalEntryDetails.fulfilled, (state, { payload }) => {
        state.isLoadingDetails = false;
        state.details = payload;
      })
      .addCase(fetchManualJournalEntryDetails.rejected, (state, { payload }) => {
        state.isLoadingDetails = false;
        state.error = payload;
      })
      .addCase(addManualJournalEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addManualJournalEntry.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.success = payload?.message ?? null;
        state.lastSavedId = payload?.data?.id ?? payload?.id ?? null;
      })
      .addCase(addManualJournalEntry.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(updateManualJournalEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateManualJournalEntry.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.success = payload?.message ?? null;
        state.lastSavedId = payload?.data?.id ?? payload?.id ?? null;
      })
      .addCase(updateManualJournalEntry.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(deleteManualJournalEntry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteManualJournalEntry.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.success = payload?.message ?? null;
      })
      .addCase(deleteManualJournalEntry.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(bulkApplyModified.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkApplyModified.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.success = payload?.message ?? null;
      })
      .addCase(bulkApplyModified.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(bulkDeleteManualJournalEntries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkDeleteManualJournalEntries.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.success = payload?.message ?? null;
      })
      .addCase(bulkDeleteManualJournalEntries.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      });
  },
});

export const { clearState, clearDetails } = slice.actions;
export default slice.reducer;
